import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Container, Eyebrow, MButton, ArrowRight, MarketingPage, PageHeader } from "./ui";
import { breadcrumb, faqJsonLd, SITE } from "./seo";

/* ─── Inline icons (stroke="currentColor", inherit accent tint) ───────────── */
type IconProps = { className?: string };

function IconShield({ className = "" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconKey({ className = "" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="8" cy="15" r="4" />
      <path d="M11 12l9-9M17 6l2 2M14 9l2 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconUsers({ className = "" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19a5.5 5.5 0 0111 0M16 6a3 3 0 010 6M17 19c0-1.6-.6-3-1.6-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconLock({ className = "" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="4.5" y="10" width="15" height="10" rx="2.5" />
      <path d="M8 10V7a4 4 0 018 0v3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 14v2.5" strokeLinecap="round" />
    </svg>
  );
}

function IconScale({ className = "" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 4v16M6 20h12M5 7l-2.5 6a3 3 0 005 0L5 7zm14 0l-2.5 6a3 3 0 005 0L19 7zM5 7l7-2 7 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconVideo({ className = "" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="6" width="12" height="12" rx="2.5" />
      <path d="M15 10l5-3v10l-5-3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconWebhook({ className = "" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="6" r="2.5" />
      <circle cx="6" cy="17" r="2.5" />
      <circle cx="18" cy="17" r="2.5" />
      <path d="M10.5 8l-3 6M13.5 8l3 6M8.5 17h7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconGlobe({ className = "" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.4 3.8 5.6 3.8 9S14.5 18.6 12 21c-2.5-2.4-3.8-5.6-3.8-9S9.5 5.4 12 3z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCheck({ className = "" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="3.5">
      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ─── Reusable bits ───────────────────────────────────────────────────────── */
function IconBadge({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-warm-accent/10 text-warm-accent">
      {children}
    </span>
  );
}

type Section = {
  icon: ReactNode;
  eyebrow: string;
  title: string;
  points: string[];
};

const SECTIONS: Section[] = [
  {
    icon: <IconKey className="h-5 w-5" />,
    eyebrow: "Authentication",
    title: "Hardened sign-in and sessions",
    points: [
      "Passwords hashed with PBKDF2-SHA256 — 100,000 iterations and a unique 16-byte per-user salt, computed with WebCrypto at the edge.",
      "Short-lived JWT access tokens (HS256) with a 15-minute lifetime.",
      "Rotating refresh tokens kept in httpOnly cookies, SHA-256 hashed at rest and revoked the moment they rotate.",
      "Optional TOTP two-factor authentication — recommended for everyone and required for clinicians and admins who handle protected health information.",
      "Single-use, time-limited tokens for email verification (24h), password reset (1h) and team invites (7d) — hashed in the database and marked used after consumption.",
    ],
  },
  {
    icon: <IconUsers className="h-5 w-5" />,
    eyebrow: "Tenant & patient isolation",
    title: "Access scoped to the clinic — and the patient",
    points: [
      "Role-based access control with three roles — owner > admin > member — alongside dedicated platform admins.",
      "Every database query is scoped to your clinic. There are no cross-tenant reads.",
      "Per-patient data scoping enforces minimum-necessary access, so staff see only the patients they're caring for.",
      "An immutable audit log records every access and change to protected health information.",
      "Last-owner protection prevents anyone from accidentally locking an organization out of its own account.",
    ],
  },
  {
    icon: <IconLock className="h-5 w-5" />,
    eyebrow: "Secrets & encryption",
    title: "PHI and provider keys encrypted",
    points: [
      "Third-party provider keys (video, email, billing, model gateway) are encrypted at rest with 256-bit AES-GCM. The master key lives only in the Worker environment, and keys are never returned to the UI — admins see only whether a key is configured.",
      "Data is encrypted in transit everywhere, and the most sensitive protected health information is protected with field-level encryption on top of at-rest encryption.",
      "Per-endpoint input validation with Zod runs on every request.",
      "Rate limiting protects auth and patient endpoints, backed by Cloudflare edge protection.",
    ],
  },
  {
    icon: <IconShield className="h-5 w-5" />,
    eyebrow: "Clinical safety",
    title: "Information and triage — never a diagnosis the machine makes alone",
    points: [
      "The AI doctor provides information and triage. It does not make an autonomous diagnosis or issue a prescription on its own.",
      "Emergency red-flag detection — symptoms and vitals such as chest pain, SpO₂ below 90 or suicidal ideation — escalates the visit to a human clinician immediately.",
      "Every answer cites the report or finding it's based on, and never invents a value it cannot support. Imaging produces observations for clinician review, not a verdict.",
      "Patients are always told they are speaking with an AI, and a licensed clinician reviews and signs off on the visit where required.",
    ],
  },
  {
    icon: <IconVideo className="h-5 w-5" />,
    eyebrow: "Patient data & recordings",
    title: "Recordings stay private",
    points: [
      "Consultations are recorded only with consent, stored privately and served only through signed, expiring links.",
      "Shared visit summaries exclude the recording.",
      "Consent for telehealth, recording and data processing is captured and versioned before a visit begins.",
    ],
  },
  {
    icon: <IconWebhook className="h-5 w-5" />,
    eyebrow: "Webhooks & integrations",
    title: "Every inbound event is verified",
    points: [
      "All inbound webhooks are signature-verified — HMAC-SHA256 for the video provider, Svix signatures for email and Stripe signatures for billing.",
      "Handlers are idempotent, so provider retries are always safe to process.",
    ],
  },
  {
    icon: <IconGlobe className="h-5 w-5" />,
    eyebrow: "Infrastructure & transport",
    title: "Built on Cloudflare's edge",
    points: [
      "Runs on Cloudflare's global edge (Workers + Pages) with a managed Neon Postgres database.",
      "HTTPS everywhere with HSTS, a strict Content-Security-Policy, and modern security response headers including X-Content-Type-Options, Referrer-Policy and frame protections.",
    ],
  },
];

const GLANCE: string[] = [
  "PBKDF2 · 100k iterations",
  "AES-256 PHI at rest",
  "15-min access tokens",
  "Immutable audit log",
  "Per-patient isolation",
  "Consented, expiring recording links",
];

const COMMITMENTS: { title: string; body: string }[] = [
  {
    title: "Aligned with HIPAA & leading frameworks",
    body: "Our controls are built to align with the HIPAA Security Rule and the practices behind SOC 2 and ISO 27001 — least privilege, encryption, auditable access and isolation. We are working toward formal attestation rather than claiming it today.",
  },
  {
    title: "BAAs & data-subject requests",
    body: "We sign Business Associate Agreements with the vendors that touch protected health information, and design with data-minimization in mind, in line with HIPAA and GDPR. Reach out and we will help patients access, export (FHIR or JSON) or delete their data.",
  },
  {
    title: "Building toward formal compliance",
    body: "Compliance is a roadmap commitment for us. We continue to harden controls and document them so that audited attestations become a milestone, not a marketing line — we never claim certifications we don't yet hold.",
  },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: "Who can see consultation recordings?",
    a: "Only authorized members of your clinic, scoped to the patients they care for. Recordings are captured only with consent, live in private storage and are served exclusively through signed, expiring links. Shared visit summaries never include the recording.",
  },
  {
    q: "How are payment details handled?",
    a: "Card data is handled by Stripe. We never store full card numbers — billing webhooks are Stripe signature-verified and idempotent, so payment processing stays in Stripe's PCI-compliant environment.",
  },
  {
    q: "How is protected health information protected?",
    a: "PHI is encrypted in transit and at rest with 256-bit AES-GCM, with field-level encryption on the most sensitive data. Access is scoped per patient on a minimum-necessary basis, and every access or change is written to an immutable audit log.",
  },
  {
    q: "What stops one clinic from seeing another's patients?",
    a: "Every database query is scoped to your clinic through role-based access control, with per-patient scoping on top. There are no cross-tenant reads, and last-owner protection keeps your organization from being locked out.",
  },
];

/* ─── Page ────────────────────────────────────────────────────────────────── */
export function SecurityPage() {
  return (
    <MarketingPage
      seo={{
        title: "Security & Trust — WonderfulDoctor",
        description:
          "How WonderfulDoctor protects patient health data: HIPAA-aligned controls, 256-bit AES-GCM encryption, per-patient access scoping, an immutable audit log, clinical-safety guardrails and consented, private recordings.",
        path: "/security",
        jsonLd: [
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Security", path: "/security" },
          ]),
          faqJsonLd(FAQS),
        ],
      }}
    >
      <PageHeader
        eyebrow="SECURITY & TRUST"
        title="Security woven into"
        accent="every visit"
        sub="From hashed passwords at the edge to consented, expiring recording links, WonderfulDoctor is engineered so your patients' protected health information stays protected by default."
      >
        <MButton to="/signup" className="px-6 py-3 text-base">
          Start free <ArrowRight />
        </MButton>
        <MButton
          href="mailto:security@wonderfulagent.ai"
          variant="outline"
          className="px-6 py-3 text-base"
        >
          Report an issue
        </MButton>
      </PageHeader>

      {/* Security at a glance */}
      <section className="pb-8">
        <Container>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {GLANCE.map((g) => (
              <span
                key={g}
                className="inline-flex items-center gap-2 rounded-full border border-warm-border bg-warm-card px-4 py-2 text-sm font-medium text-warm-ink"
              >
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-warm-accent/15 text-warm-accent">
                  <IconCheck className="h-2.5 w-2.5" />
                </span>
                {g}
              </span>
            ))}
          </div>
        </Container>
      </section>

      {/* Themed practice sections A–G */}
      <section className="py-24">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>How we protect your data</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              The practices behind the platform.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-warm-muted">
              No vague assurances — these are the controls running on every account today. For the clinical-safety
              guardrails that govern each consultation, see the{" "}
              <Link to="/platform" className="font-medium text-warm-accent hover:text-warm-accentDark">
                platform overview
              </Link>
              .
            </p>
          </div>

          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            {SECTIONS.map((s) => (
              <div key={s.title} className="rounded-3xl border border-warm-border bg-warm-card p-7">
                <div className="flex items-center gap-4">
                  <IconBadge>{s.icon}</IconBadge>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-warm-accent">
                      {s.eyebrow}
                    </div>
                    <h3 className="mt-0.5 text-lg font-semibold text-warm-ink">{s.title}</h3>
                  </div>
                </div>
                <ul className="mt-5 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex gap-3 text-[15px] leading-relaxed text-warm-muted">
                      <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-warm-accent/15 text-warm-accent">
                        <IconCheck className="h-2.5 w-2.5" />
                      </span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Compliance & commitments (honest framing) */}
      <section className="bg-warm-sand/40 py-24">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>Compliance & commitments</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              Honest about where we are.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-warm-muted">
              We follow practices aligned with recognized frameworks and are building toward formal certification.
              We don't claim attestations we don't yet hold.
            </p>
          </div>
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {COMMITMENTS.map((c) => (
              <div key={c.title} className="rounded-3xl border border-warm-border bg-warm-card p-7">
                <IconBadge>
                  <IconScale className="h-5 w-5" />
                </IconBadge>
                <h3 className="mt-5 text-lg font-semibold text-warm-ink">{c.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-warm-muted">{c.body}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Responsible disclosure */}
      <section className="py-24">
        <Container>
          <div className="overflow-hidden rounded-4xl border border-warm-border bg-warm-ink p-10 text-warm-bg sm:p-14">
            <div className="max-w-2xl">
              <IconBadge>
                <IconShield className="h-5 w-5" />
              </IconBadge>
              <h2 className="mt-6 text-3xl font-bold tracking-tight sm:text-4xl">
                Found something? <span className="font-display italic font-normal text-warm-glow">Tell us.</span>
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-warm-bg/70">
                We welcome security researchers. If you believe you've found a vulnerability, email us a short
                description and steps to reproduce — we read every report, respond quickly, and work with you in
                good faith to get it fixed. Please give us a reasonable window before any public disclosure.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <MButton
                  href="mailto:security@wonderfulagent.ai"
                  className="px-6 py-3 text-base"
                >
                  Email security@wonderfulagent.ai <ArrowRight />
                </MButton>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-warm-sand/40 py-24">
        <Container>
          <div className="max-w-2xl">
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              Common questions.
            </h2>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {FAQS.map((f) => (
              <div key={f.q} className="rounded-3xl border border-warm-border bg-warm-card p-7">
                <h3 className="text-lg font-semibold text-warm-ink">{f.q}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-warm-muted">{f.a}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="py-24">
        <Container className="text-center">
          <h2 className="mx-auto max-w-2xl text-balance text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Care your patients can <span className="font-display italic font-normal text-warm-accent">trust</span>.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-warm-muted">
            Strong defaults, private recordings and clinician oversight behind every visit — from your first
            consultation at {SITE.name}.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <MButton to="/signup" className="px-6 py-3 text-base">
              Start free <ArrowRight />
            </MButton>
            <MButton href={`mailto:${SITE.email}`} variant="outline" className="px-6 py-3 text-base">
              Talk to us
            </MButton>
          </div>
        </Container>
      </section>
    </MarketingPage>
  );
}
